import React, { useEffect } from "react";
import { formatCurrency } from "./uebersichtHelpers";
import { formatDateOnly } from "../../../utils/datetime";

/**
 * Amount cell for the Barkautionen detail modal. A missing amount stays "—";
 * it is NEVER rendered as "CHF 0.00" (a held deposit with an unknown amount is
 * still a held deposit — it counts, but has no number).
 */
export function formatCashDepositAmount(value) {
  if (value == null) return "—";
  return formatCurrency(value);
}

const STATUS_LABELS = {
  held: "Erhalten, nicht zurückgezahlt",
};

export function cashDepositStatusLabel(status) {
  return STATUS_LABELS[status] || status || "—";
}

/**
 * Detail popup for the "Barkautionen (Stichtag)" KPI card.
 *
 * The header Summe / Anzahl are taken VERBATIM from the backend response
 * (cash_deposits_total / cash_deposits_count) — never summed from the visible
 * item amounts, because a legitimate NULL-amount row counts toward the count
 * but contributes 0 to the total. The header must always match the KPI card.
 */
export default function CashDepositDetailsModal({ open, onClose, loading, error, data }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const items = Array.isArray(data?.items) ? data.items : [];
  const total = data?.cash_deposits_total;
  const count = data?.cash_deposits_count;
  const asOf = data?.as_of_date;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Barkautionen Details"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-[14px] border border-black/10 bg-white dark:border-white/[0.07] dark:bg-[#141824]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-black/10 px-6 py-4 dark:border-white/[0.07]">
          <div>
            <h4 className="m-0 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]">
              Barkautionen
            </h4>
            <p className="mt-1 text-[12px] text-[#64748b] dark:text-[#93a4bf]">
              Erhalten, nicht zurückgezahlt
              {asOf ? ` · Stichtag ${formatDateOnly(asOf)}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-lg border border-black/10 px-3 py-1 text-sm text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
          >
            Schließen
          </button>
        </div>

        {/* Summary — ALWAYS from the backend response, never summed client-side. */}
        <div className="flex flex-wrap gap-6 border-b border-black/10 px-6 py-3 dark:border-white/[0.07]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5px] text-[#64748b] dark:text-[#4a5070]">
              Summe
            </div>
            <div className="font-mono text-[18px] font-medium text-fuchsia-600 dark:text-fuchsia-400">
              {typeof total === "number" ? formatCurrency(total) : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.5px] text-[#64748b] dark:text-[#4a5070]">
              Anzahl
            </div>
            <div className="font-mono text-[18px] font-medium text-[#0f172a] dark:text-[#eef2ff]">
              {typeof count === "number"
                ? `${count} ${count === 1 ? "Barkaution" : "Barkautionen"}`
                : "—"}
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-auto px-6 py-4">
          {loading ? (
            <p className="text-sm text-[#64748b] dark:text-[#93a4bf]">Lade …</p>
          ) : error ? (
            <p className="text-sm text-[#dc2626] dark:text-[#f87171]">{error}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-[#64748b] dark:text-[#93a4bf]">
              Keine gehaltenen Barkautionen für diesen Stichtag.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-[12px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.5px] text-[#64748b] dark:text-[#4a5070]">
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Mieter</th>
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Unit</th>
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Raum</th>
                    <th className="border-b border-black/10 px-2 py-2 text-right dark:border-white/[0.07]">Betrag</th>
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Erhalten am</th>
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Status</th>
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Mietbeginn</th>
                    <th className="border-b border-black/10 px-2 py-2 dark:border-white/[0.07]">Mietende</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.tenancy_id} className="text-[#334155] dark:text-[#8892b0]">
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {it.tenant_name || "—"}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {it.unit_title || "—"}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {it.room_name || "—"}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 text-right font-mono dark:border-white/[0.04]">
                        {formatCashDepositAmount(it.amount_chf)}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {formatDateOnly(it.deposit_received_at)}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {cashDepositStatusLabel(it.status)}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {formatDateOnly(it.move_in_date)}
                      </td>
                      <td className="border-b border-black/5 px-2 py-2 dark:border-white/[0.04]">
                        {formatDateOnly(it.move_out_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
